import { useEffect, useMemo, useRef } from "react";
import type Application from "../../types/application";
import { Body, SecondaryBody } from "@sprout/typography";
import CompanyLogo from "@components/company_logo";
import { Row, Stack } from "@sprout/layout";
import { Tag } from "@sprout/tag.tsx";
import theme from "@/stylesheets/theme.module.scss";
import useTranslation from "@hooks/use_translation";
import { useTimeAgo, composeStatusLabel } from "@utils/time_ago";
import { IconButton } from "@/sprout/icon_button";
import PreviewIcon from "@/sprout/icons/preview";
import CheckMarkCircleIcon from "@sprout/icons/check_mark_circle";
import { router, usePage } from "@inertiajs/react";
import Tooltip from "@/sprout/tooltip";
import ApplicationCardTasks from "./application_card_tasks";
import type { GhrTask } from "@/types/task";
import useTasks from "@hooks/use_tasks.tsx";

type Props = {
  application: Application;
  selected: boolean;
  onApplicationSelect: (id: number) => void;
  narrow?: boolean;
  addLink?: boolean;
  readOnly?: boolean;
  expandTasksAsPopover?: boolean;
};

const ApplicationCard = ({
  application,
  selected,
  onApplicationSelect,
  narrow = false,
  addLink = false,
  readOnly = false,
  expandTasksAsPopover = false,
}: Props) => {
  const { jobSeekersTasksEnabled } = usePage<{ jobSeekersTasksEnabled?: boolean }>().props;
  const { t } = useTranslation("user");
  const { t: tApplications } = useTranslation("applications");
  const appliedLabel = composeStatusLabel(
    tApplications("applied"),
    useTimeAgo(application.applied_at),
  );
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  const { data: tasks } = useTasks();
  const pendingTasks = useMemo(
    () =>
      tasks.flatMap((task) =>
        (task as GhrTask).application_id === application.id ? [task as GhrTask] : [],
      ),
    [tasks, application.id],
  );

  const logo = (
    <div className="application-card-left">
      <CompanyLogo
        logoUrl={application.company_logo_url}
        companyName={application.company_name}
        size={narrow ? "small" : "smaller"}
      />
    </div>
  );

  const tags = (
    <>
      {application.dream_job && (
        <div className="application-card-tag">
          <Tag label={t("dream_job")} color={theme.marigoldE50} />
        </div>
      )}
      {application.duplicate && (
        <div className="application-card-tag">
          <Tag label={t("duplicate_application")} color={theme.smokeGray} />
        </div>
      )}
    </>
  );

  const title = (
    <Body medium className={narrow ? "!text-sm" : ""}>
      {application.job_title}
    </Body>
  );

  const company = (
    <div className="flex items-center gap-1 w-full min-w-0">
      <SecondaryBody className="min-w-0">{application.company_name}</SecondaryBody>
      {application.sharingStatus && (
        <CheckMarkCircleIcon size="xs" viewBox="0 0 16 16" color="green" />
      )}
    </div>
  );

  const statusBody = (
    <SecondaryBody metadata className={narrow ? "!text-xs leading-[1.3rem]" : ""}>
      {appliedLabel}
    </SecondaryBody>
  );

  const details =
    jobSeekersTasksEnabled && !application.inactive ? (
      <ApplicationCardTasks
        application={application}
        pendingTasks={pendingTasks}
        fallback={statusBody}
        expandAsPopover={expandTasksAsPopover}
      />
    ) : (
      statusBody
    );

  const link = addLink && (
    <div className="h-full content-center">
      <Tooltip text={t("application_icon_tooltip")}>
        <IconButton
          icon={PreviewIcon}
          onClick={(e) => {
            e.stopPropagation();
            router.visit(`/applications/${application.id}`);
          }}
          label={t("application_icon_tooltip")}
          size="md"
        />
      </Tooltip>
    </div>
  );

  return (
    <div
      ref={cardRef}
      className={`application-card ${selected ? "selected" : ""} ${narrow ? "narrow" : ""} ${readOnly ? "pointer-events-none" : ""}`}
      onClick={() => !readOnly && onApplicationSelect(application.id)}
    >
      {narrow ? (
        <Stack gap="16px" className="!flex-row">
          <Row gap="12px" className="basis-[content]">
            {logo}
            {tags}
          </Row>
          <Stack gap="0">
            {title}
            {company}
            {details}
          </Stack>
          {link}
        </Stack>
      ) : (
        <Stack gap="24px">
          <Stack gap="8px">
            <div className="application-card__tags">{tags}</div>
            <Row gap="12px" className="!items-stretch">
              {logo}
              <Stack gap="4px" className="flex-1 min-w-0">
                {title}
                {company}
              </Stack>
              {link}
            </Row>
          </Stack>
          {details}
        </Stack>
      )}
    </div>
  );
};

export default ApplicationCard;
